/**
 * DisputeCommunicationPanel - Direct dedicated communication channel
 *
 * DevStory 4.5:
 * Criterio 9 - Canal de comunicación creado automáticamente asociado a la disputa
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@components/ui';
import { useSendDisputeMessage } from '@hooks/useDisputes';
import type { Dispute, DisputeMessage } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { pendingDisputeEntry } from '@/api/adapter/disputes';

interface DisputeCommunicationPanelProps {
  dispute: Dispute;
  messages: DisputeMessage[];
  isClosed?: boolean;
  canWrite?: boolean;
}

export function DisputeCommunicationPanel({
  dispute,
  messages,
  isClosed = false,
  canWrite = false,
}: DisputeCommunicationPanelProps): JSX.Element {
  const account = useAuthStore((s) => s.session?.accountId);
  const [text, setText] = useState(
    () => pendingDisputeEntry(dispute.id, 'messages')?.content ?? ''
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const sendMessageMutation = useSendDisputeMessage();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = text.trim();
      if (!trimmed || isClosed || !canWrite) return;

      sendMessageMutation.mutate(
        { disputeId: dispute.id, content: trimmed },
        {
          onSuccess: () => {
            setText('');
          },
        }
      );
    },
    [text, dispute.id, sendMessageMutation, isClosed, canWrite]
  );

  return (
    <div className="flex h-[520px] flex-col rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)]">
      {/* Channel Header */}
      <div className="flex items-center justify-between border-b border-[var(--base-border,#3d3d3d)] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[rgba(54,211,153,0.2)] bg-[rgba(54,211,153,0.12)] text-[var(--state-brand-active,#36d399)]">
            <i className="ri-chat-voice-line text-lg" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white">
              Dispute Communication Channel
            </h4>
            <p className="text-[11px] text-[var(--text-dark-secondary,rgba(255,255,255,0.6))]">
              Private project channel · Channel ID:{' '}
              {dispute.communicationId || dispute.id}
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
          Active
        </span>
      </div>

      {/* Messages list */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center space-y-2 text-center text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
            <i className="ri-message-3-line text-3xl" />
            <p>
              No direct messages yet. Send a message to communicate with the
              counterparty.
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = account === msg.senderId;

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[85%] ${
                  isMe ? 'ml-auto' : 'mr-auto'
                }`}
              >
                <div className="mb-1 flex items-center gap-1.5 text-[10px] text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
                  <span className="font-semibold text-white">
                    {msg.senderName}
                  </span>
                  <span className="rounded bg-white/10 px-1 text-[9px] uppercase text-gray-300">
                    {msg.senderRole}
                  </span>
                  <span>
                    ·{' '}
                    {new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                <div
                  className={`rounded-2xl px-4 py-2.5 text-xs leading-relaxed ${
                    isMe
                      ? 'rounded-br-none bg-[var(--state-brand-active,#36d399)] font-medium text-[#141414]'
                      : 'rounded-bl-none border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] text-white'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {sendMessageMutation.error && (
        <p role="alert" className="px-4 py-2 text-xs text-rose-400">
          {sendMessageMutation.error.message}
        </p>
      )}
      {/* Message input */}
      {!isClosed && canWrite ? (
        <form
          onSubmit={handleSend}
          className="flex items-center gap-2 border-t border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-3"
        >
          <input
            type="text"
            aria-label="Dispute message"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message regarding this dispute..."
            className="flex-1 bg-transparent px-3 py-2 text-xs text-white placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.4))] focus:outline-none"
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={sendMessageMutation.isPending}
            disabled={sendMessageMutation.isPending || !text.trim()}
            className="shrink-0 text-xs"
          >
            <i className="ri-send-plane-fill mr-1" />
            Send
          </Button>
        </form>
      ) : (
        <div className="border-t border-[var(--base-border,#3d3d3d)] p-3 text-center text-xs text-gray-400">
          {isClosed
            ? 'Channel archived following resolution.'
            : 'You do not have permission to publish in this channel.'}
        </div>
      )}
    </div>
  );
}
